<script setup>
import { computed, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { ArrowRight, Building2, Check, Coins, Loader2, Minus } from 'lucide-vue-next'
import { TIERS, ENTERPRISE, OVERAGE_CENTS, priceFor, perPageCents, annualSavingsPct } from '@/data/pricing'
import { useAuthStore } from '@/stores/authStore'
import { usePlanStore } from '@/stores/planStore'
import RedirectOverlay from '@/components/billing/RedirectOverlay.vue'
import { usePolar } from '@/composables/usePolar'
import { usePageSeo } from '@/composables/usePageSeo'

usePageSeo({
  // WebApplication (with offers) is emitted sitewide from App.vue — no per-page schema needed.
  title: 'Pricing — Page-Based Document AI Plans',
  description: 'Page-based pricing that scales with your stack. Free for 5 pages/month — Solo, Business, and Enterprise (5 users + API) plans with annual billing and metered overage.',
  path: '/pricing',
})

const router = useRouter()
const auth = useAuthStore()
const plan = usePlanStore()
const polar = usePolar()

const interval = ref('month') // 'month' | 'year'

const cards = computed(() =>
  TIERS.map((t) => ({
    ...t,
    display: priceFor(t, interval.value),
    savingsPct: interval.value === 'year' ? annualSavingsPct(t) : null,
    centsPerPage: perPageCents(t, interval.value),
    isCurrent: auth.isAuthenticated && plan.tier === t.id,
  })),
)


function choose(tier) {
  if (!auth.isAuthenticated) {
    router.push('/signup')
    return
  }
  if (tier.id === 'free' || tier.isCurrent) {
    router.push('/dashboard')
    return
  }
  if (plan.isMockMode) {
    plan.setTier(tier.id, interval.value)
    router.push('/dashboard')
    return
  }
  redirectingTier.value = tier.id
  // Existing paid subscribers change plans via the Polar portal.
  const nav = plan.isPaid
    ? polar.openPortal()
    : polar.redirectToCheckout(polar.productKey(tier.id, interval.value))
  nav.catch(() => { redirectingTier.value = null })
}

// Which card's button was clicked — inline spinner + full-screen overlay.
const redirectingTier = ref(null)

const COMPARISON_ROWS = [
  { label: 'Pages / month', value: (t) => t.pages.toLocaleString() },
  { label: 'Annual billing (2 months free)', value: (t) => (t.annual !== null ? `$${t.annual}/yr` : null) },
  { label: 'JSON + CSV export', value: () => true },
  { label: 'Overage rate (you set the budget)', value: (t) => (OVERAGE_CENTS[t.id] ? `${OVERAGE_CENTS[t.id]}¢/page` : null) },
  { label: 'Bulk export', value: (t) => ['business', 'enterprise'].includes(t.id) },
  { label: 'Email support', value: (t) => ['business', 'enterprise'].includes(t.id) },
  { label: 'API access', value: (t) => (t.id === 'enterprise' ? true : null) },
  { label: 'Users', value: (t) => (t.id === 'enterprise' ? '5 (+5-user blocks)' : '1') },
  { label: 'SSO / DPA', value: () => null },
]

const FAQ = [
  {
    q: 'What counts as a page?',
    a: 'One side of one sheet. A 5-page PDF uses 5 pages of your quota; a photo of a receipt uses 1. Page counts are checked before processing, so you never pay quota for a failed extraction.',
  },
  {
    q: 'What happens when I run out of pages?',
    a: 'On a paid plan you can keep going with pay-as-you-go overage — a simple per-page rate, capped by a monthly budget you set, with the exact cost confirmed before each document. No budget set, no charges: uploads simply pause until your monthly reset.',
  },
  {
    q: 'Can overage surprise me on my bill?',
    a: 'No. Overage is off until you set a budget, every over-quota document shows its cost before processing, your budget is a hard cap, and you choose an alert threshold (50/80/90%).',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancelling stops future charges and you keep full access until the end of the period you already paid for.',
  },
  {
    q: 'Where is my data stored?',
    a: 'Documents and extracted data are stored encrypted in AWS (us-east-1) and automatically deleted after your plan\u2019s retention window — 30 days on Free, 90 on Solo, 180 on Business, 365 on Enterprise. You can delete any document — or your whole account — immediately at any time.',
  },
  {
    q: 'How do Enterprise seats work?',
    a: 'Enterprise includes 5 users: 1 admin plus 4 members you invite by email. Everyone shares the pooled page quota while keeping their own private workspace. Need more people? Add 5-user blocks ($35/mo) — each also adds 1,000 pooled pages. The admin manages members, the overage budget, and API keys from Settings.',
  },
  {
    q: 'What can I do with the API?',
    a: 'Upload documents and retrieve extracted JSON programmatically — see the API docs. API usage draws from the same pooled page quota, with sensible rate limits (10 requests/min).',
  },
  {
    q: 'What’s the difference between monthly and annual billing?',
    a: 'Annual plans cost 10× the monthly price for 12 months of service — two months free. The page quota stays monthly either way.',
  },
  {
    q: 'What document types are supported?',
    a: 'Invoices, receipts, purchase orders, utility bills, and financial reports — each with a tuned extraction schema. Anything else is processed with a general schema, and we add new types regularly.',
  },
]
</script>

<template>
  <section class="container-wide py-16">
    <!-- Header -->
    <header class="mx-auto max-w-2xl text-center">
      <h1 class="font-display text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
        Pricing that scales with your stack.
      </h1>
      <p class="mt-3 text-lg text-slate-600 dark:text-slate-300">
        Start free. Pay for pages, not seats. Cancel anytime.
      </p>

      <!-- Billing toggle -->
      <div class="mt-6 inline-flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-700" role="radiogroup" aria-label="Billing interval">
        <button
          type="button"
          role="radio"
          :aria-checked="interval === 'month'"
          class="rounded-md px-5 py-2 text-sm font-medium transition"
          :class="interval === 'month' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 dark:text-slate-300'"
          @click="interval = 'month'"
        >
          Monthly
        </button>
        <button
          type="button"
          role="radio"
          :aria-checked="interval === 'year'"
          class="flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-medium transition"
          :class="interval === 'year' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 dark:text-slate-300'"
          @click="interval = 'year'"
        >
          Annual
          <span class="pill text-[10px]" :class="interval === 'year' ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'">2 months free</span>
        </button>
      </div>
    </header>

    <!-- Tier cards -->
    <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      <div
        v-for="tier in cards"
        :key="tier.id"
        class="card-surface relative flex flex-col p-6"
        :class="tier.featured ? 'ring-2 ring-primary' : ''"
      >
        <span
          v-if="tier.featured"
          class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-white"
        >
          Most popular
        </span>

        <div class="flex items-baseline justify-between">
          <h2 class="font-display text-lg font-semibold" :class="tier.featured ? 'text-primary' : ''">{{ tier.name }}</h2>
          <span v-if="tier.isCurrent" class="pill bg-primary/10 text-xs text-primary">Current</span>
        </div>

        <p class="mt-3 text-3xl font-bold transition-all duration-150">
          {{ tier.display.amount }}<span v-if="tier.display.per" class="text-base font-normal text-slate-500">{{ tier.display.per }}</span>
        </p>
        <p class="h-4 text-xs text-slate-500 dark:text-slate-400">
          {{ tier.display.note }}<span v-if="tier.savingsPct" class="ml-1 font-medium text-emerald-600 dark:text-emerald-400">· save {{ tier.savingsPct }}%</span>
        </p>

        <p class="mt-3 border-b border-slate-100 pb-4 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">
          <strong class="text-slate-900 dark:text-white">{{ tier.pages.toLocaleString() }}</strong> pages/month
          <span v-if="tier.centsPerPage" class="ml-1 text-xs text-slate-400">({{ tier.centsPerPage }}¢/page)</span>
        </p>

        <ul class="mt-4 flex-1 space-y-2 text-sm">
          <li v-for="feat in tier.features" :key="feat" class="flex items-start gap-2">
            <Check class="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            {{ feat }}
          </li>
        </ul>

        <button
          type="button"
          class="mt-6 flex w-full items-center justify-center gap-2"
          :class="tier.featured ? 'btn-primary' : 'btn-secondary'"
          :disabled="tier.isCurrent || polar.isRedirecting.value"
          @click="choose(tier)"
        >
          <template v-if="redirectingTier === tier.id"><Loader2 class="h-4 w-4 animate-spin" /> Opening checkout…</template>
          <template v-else-if="tier.isCurrent">Current plan</template>
          <template v-else-if="tier.id === 'free'">Get started free</template>
          <template v-else>Start with {{ tier.name }}</template>
        </button>
      </div>
    </div>

    <!-- Enterprise -->
    <div class="card-surface mt-6 flex flex-wrap items-center justify-between gap-5 p-6">
      <div class="flex items-start gap-4">
        <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Building2 class="h-5 w-5" />
        </div>
        <div>
          <h2 class="font-display text-lg font-semibold">{{ ENTERPRISE.name }}</h2>
          <p class="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-400">{{ ENTERPRISE.body }}</p>
        </div>
      </div>
      <a class="btn-secondary shrink-0" :href="ENTERPRISE.href">{{ ENTERPRISE.cta }}</a>
    </div>

    <!-- Pay as you go (pricing v3) -->
    <div class="mt-16">
      <header class="mx-auto max-w-2xl text-center">
        <h2 class="font-display text-2xl font-bold">Need a burst? Pay as you go.</h2>
        <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Paid plans can process beyond their quota at a simple per-page rate — under a monthly
          budget you control, with the exact cost shown before every document. No surprises, ever.
        </p>
      </header>
      <div class="mx-auto mt-8 grid max-w-3xl gap-4 sm:grid-cols-3">
        <div v-for="t in TIERS.filter((x) => OVERAGE_CENTS[x.id])" :key="t.id" class="card-surface flex flex-col items-center p-6 text-center">
          <Coins class="h-6 w-6 text-primary" />
          <p class="mt-3 font-display text-xl font-bold">{{ t.name }}</p>
          <p class="mt-1 text-2xl font-bold">{{ OVERAGE_CENTS[t.id] }}¢<span class="text-sm font-normal text-slate-500">/page</span></p>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">beyond {{ t.pages.toLocaleString() }} plan pages</p>
        </div>
      </div>
      <p class="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
        Overage is off by default — enable it by setting a budget in Settings. Alerts at 50/80/90%, your pick.
      </p>
    </div>

    <!-- Comparison table (desktop) -->
    <div class="mt-16 hidden md:block">
      <h2 class="text-center font-display text-2xl font-bold">Compare plans</h2>
      <div class="card-surface mt-8 overflow-x-auto p-0">
        <table class="w-full text-sm">
          <thead class="sticky top-0 z-10 bg-white dark:bg-slate-900">
            <tr class="border-b border-slate-200 dark:border-slate-700">
              <th class="px-5 py-4 text-left font-medium text-slate-500 dark:text-slate-400">Feature</th>
              <th v-for="tier in TIERS" :key="tier.id" class="px-5 py-4 text-center font-display font-semibold">{{ tier.name }}</th>
              <th class="px-5 py-4 text-center font-display font-semibold">Enterprise Custom</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in COMPARISON_ROWS" :key="row.label" class="border-b border-slate-100 last:border-0 dark:border-slate-800">
              <td class="px-5 py-3 text-slate-600 dark:text-slate-300">{{ row.label }}</td>
              <td v-for="tier in TIERS" :key="tier.id" class="px-5 py-3 text-center">
                <Check v-if="row.value(tier) === true" class="mx-auto h-4 w-4 text-emerald-500" />
                <Minus v-else-if="!row.value(tier)" class="mx-auto h-4 w-4 text-slate-300 dark:text-slate-600" />
                <span v-else class="font-medium">{{ row.value(tier) }}</span>
              </td>
              <td class="px-5 py-3 text-center font-medium text-slate-500">Custom</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Comparison (mobile accordion) -->
    <div class="mt-16 md:hidden">
      <h2 class="text-center font-display text-2xl font-bold">Compare plans</h2>
      <div class="mt-6 space-y-3">
        <details v-for="tier in TIERS" :key="tier.id" class="card-surface group p-0">
          <summary class="flex cursor-pointer items-center justify-between p-4 font-display font-semibold">
            {{ tier.name }}
            <span class="text-sm font-normal text-slate-500">{{ priceFor(tier, interval).amount }}{{ priceFor(tier, interval).per }}</span>
          </summary>
          <dl class="border-t border-slate-100 px-4 py-3 text-sm dark:border-slate-800">
            <div v-for="row in COMPARISON_ROWS" :key="row.label" class="flex items-center justify-between py-1.5">
              <dt class="text-slate-600 dark:text-slate-400">{{ row.label }}</dt>
              <dd>
                <Check v-if="row.value(tier) === true" class="h-4 w-4 text-emerald-500" />
                <Minus v-else-if="!row.value(tier)" class="h-4 w-4 text-slate-300 dark:text-slate-600" />
                <span v-else class="font-medium">{{ row.value(tier) }}</span>
              </dd>
            </div>
          </dl>
        </details>
      </div>
    </div>

    <!-- FAQ -->
    <div class="mx-auto mt-16 max-w-2xl">
      <h2 class="text-center font-display text-2xl font-bold">Questions, answered</h2>
      <div class="mt-6 space-y-3">
        <details v-for="item in FAQ" :key="item.q" class="card-surface group p-0">
          <summary class="cursor-pointer p-4 font-medium">{{ item.q }}</summary>
          <p class="border-t border-slate-100 px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">
            {{ item.a }}
          </p>
        </details>
      </div>
    </div>

    <!-- Final CTA -->
    <div class="mt-20 text-center">
      <h2 class="font-display text-3xl font-bold">Stop typing. Start snapping.</h2>
      <p class="mt-2 text-slate-600 dark:text-slate-400">Free for {{ TIERS[0].pages }} pages/month — no credit card required.</p>
      <RouterLink to="/signup" class="btn-primary mt-6 inline-flex px-6 py-3 text-base">
        Get started free
        <ArrowRight class="h-4 w-4" />
      </RouterLink>
    </div>
  </section>

  <RedirectOverlay
    :show="polar.isRedirecting.value"
    :label="plan.isPaid ? 'Opening your billing portal…' : 'Opening secure checkout…'"
  />
</template>
